import { useCallback, useEffect, useRef, useState } from "react";
import { Search, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import {
  deleteMediaAsset,
  fetchMediaQuota,
  formatBytes,
  listMedia,
  normalizeMediaQuota,
  resolveMediaUrl,
  uploadMediaFile,
} from "@/lib/media-api";

export default function MediaLibrary() {
  const [items, setItems] = useState([]);
  const [quota, setQuota] = useState(null);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const inputRef = useRef(null);

  const refreshQuota = useCallback(async () => {
    try {
      const data = await fetchMediaQuota();
      setQuota(data);
    } catch {
      /* optional */
    }
  }, []);

  const load = useCallback(async (opts = {}) => {
    const nextPage = opts.page ?? 1;
    const query = opts.q ?? q;
    const filterKind = opts.kind ?? kind;
    setLoading(true);
    try {
      const [listData] = await Promise.all([
        listMedia({ kind: filterKind, q: query, page: nextPage, pageSize: 48 }),
        refreshQuota(),
      ]);
      const rows = Array.isArray(listData.items) ? listData.items : [];
      setItems((prev) => (nextPage === 1 ? rows : [...prev, ...rows]));
      setHasMore(Boolean(listData.hasMore));
      setPage(nextPage);
      if (listData.quota) setQuota(normalizeMediaQuota(listData.quota));
    } catch (err) {
      toast.error(err?.message || "Could not load media");
    } finally {
      setLoading(false);
    }
  }, [kind, q, refreshQuota]);

  useEffect(() => {
    load({ page: 1 });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onSearch = (ev) => {
    ev.preventDefault();
    load({ page: 1, q });
  };

  const onUpload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        await uploadMediaFile(file);
      }
      toast.success("Upload complete");
      await load({ page: 1 });
    } catch (err) {
      toast.error(err?.message || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const onDelete = async (asset) => {
    if (!asset?.id) return;
    if (!window.confirm(`Remove "${asset.filename}" from the workspace library?`)) return;
    try {
      await deleteMediaAsset(asset.id);
      setItems((prev) => prev.filter((a) => a.id !== asset.id));
      await refreshQuota();
      toast.success("Asset removed");
    } catch (err) {
      toast.error(err?.message || "Delete failed");
    }
  };

  const used = quota?.usedBytes ?? 0;
  const limit = quota?.limitBytes ?? 0;
  const pct = quota?.usedPct ?? (limit > 0 ? (used / limit) * 100 : 0);

  return (
    <div className="p-8 lg:p-12 max-w-[1400px] mx-auto" data-testid="media-library">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-overline">Workspace</div>
          <h1 className="font-heading text-4xl mt-2 tracking-tight font-medium">Media library</h1>
          <p className="text-sm text-[var(--text-secondary)] mt-2 max-w-xl">
            Images and PDFs shared across Page Builder, badges, wallet passes, and event branding. Email editor uploads stay separate.
          </p>
        </div>
        <button type="button" className="btn-primary" disabled={uploading} onClick={() => inputRef.current?.click()}>
          <Upload size={14} /> {uploading ? "Uploading…" : "Upload"}
        </button>
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept="image/*,.pdf,application/pdf"
          multiple
          onChange={(ev) => onUpload(ev.target.files)}
        />
      </div>

      {quota ? (
        <div className="mt-6 surface p-4">
          <div className="flex flex-wrap justify-between gap-2 text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)]">
            <span>Storage · {quota.plan || "plan"}</span>
            <span>
              {formatBytes(used)} / {formatBytes(limit)} ({pct.toFixed(1)}%)
            </span>
          </div>
          <div
            className="mt-2 h-2 bg-[var(--bg-elev)] overflow-hidden rounded-sm"
            role="progressbar"
            aria-valuenow={Math.round(pct)}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            {pct > 0 ? (
              <div
                className="h-full bg-[var(--primary)] transition-[width] duration-300"
                style={{ width: `${Math.min(100, pct)}%` }}
              />
            ) : null}
          </div>
          <p className="text-xs text-[var(--text-tertiary)] mt-2">
            {quota.plan === "enterprise"
              ? "Your plan includes 10 GB of workspace media storage."
              : `${quota.plan || "Studio"} plan · Video uploads are not supported.`}
          </p>
        </div>
      ) : null}

      <div className="mt-6 flex flex-wrap gap-2 items-center">
        <form onSubmit={onSearch} className="flex flex-1 min-w-[min(100%,280px)] max-w-xl gap-2 items-center">
          <div className="relative flex-1 min-w-0">
            <input
              className="input-dark input-dark--with-icon w-full"
              placeholder="Search…"
              value={q}
              onChange={(ev) => setQ(ev.target.value)}
              aria-label="Search media library"
            />
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)] pointer-events-none"
              aria-hidden
            />
          </div>
          <button type="submit" className="btn-secondary text-xs shrink-0">
            Search
          </button>
        </form>
        <select
          className="input-dark text-xs min-w-[9rem] shrink-0"
          value={kind}
          onChange={(ev) => {
            setKind(ev.target.value);
            load({ page: 1, kind: ev.target.value });
          }}
        >
          <option value="">All types</option>
          <option value="image">Images</option>
          <option value="document">Documents</option>
        </select>
      </div>

      <div className="mt-6">
        {loading && !items.length ? <p className="text-sm text-[var(--text-secondary)]">Loading…</p> : null}
        {!loading && !items.length ? (
          <p className="text-sm text-[var(--text-secondary)]">No assets yet. Upload files to reuse in builders.</p>
        ) : null}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {items.map((asset) => {
            const thumb = resolveMediaUrl(asset.thumbUrl || asset.url);
            return (
              <div key={asset.id} className="surface border border-[var(--border-default)] overflow-hidden group">
                <div className="aspect-square bg-[var(--bg-elev)] relative">
                  {asset.kind === "image" ? (
                    <img src={thumb} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full grid place-items-center font-mono text-xs text-[var(--text-tertiary)]">PDF</div>
                  )}
                  <button
                    type="button"
                    className="absolute top-2 right-2 p-1.5 bg-black/70 text-white opacity-0 group-hover:opacity-100 transition"
                    onClick={() => onDelete(asset)}
                    aria-label="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                <div className="p-2 border-t border-[var(--border-default)]">
                  <div className="text-xs truncate" title={asset.filename}>{asset.filename}</div>
                  <div className="text-[10px] font-mono text-[var(--text-tertiary)] mt-0.5">
                    {formatBytes(asset.bytes)} · {asset.kind}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {hasMore ? (
          <button type="button" className="btn-secondary mt-6 w-full justify-center" disabled={loading} onClick={() => load({ page: page + 1 })}>
            Load more
          </button>
        ) : null}
      </div>
    </div>
  );
}
